/**
 * EPL Session Runner - Interface para continuar sesiones activas
 * 
 * Funcionalidades:
 * - Cargar sesión existente por ID
 * - Mostrar progress y estado de la sesión
 * - Continuar desde donde se quedó
 * - Mostrar tiempo transcurrido y restante
 * - Auto-save automático
 * - Ver resultados si ya está completada
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  Play, 
  Save, 
  Clock, 
  CheckCircle,
  AlertCircle,
  Loader,
  ArrowLeft,
  RefreshCw,
  Eye,
  BarChart3,
  Calendar,
  User,
  FileText
} from 'lucide-react';

import { eplApi } from '../services/epl_api';
import { useEplLab, useEplSession } from '../hooks/useEpl';

const EplSessionRunner = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Cargar datos de sesión al montar
  useEffect(() => {
    loadSessionData();
  }, [sessionId]);

  const loadSessionData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await eplApi.getSessionState(sessionId);
      setSessionData(response.data);
    } catch (err) {
      setError(err.message);
      console.error('Error loading session:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await loadSessionData();
    } finally {
      setRefreshing(false);
    }
  };

  const handleContinue = () => {
    if (sessionData?.labId) {
      // Redirigir al lab runner con parámetro de sesión
      navigate(`/epl/lab/${sessionData.labId}?session=${sessionId}`);
    }
  };

  const handleViewResults = () => {
    // TODO: Implementar vista de resultados detallados
    console.log('Ver resultados:', sessionData);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No disponible';
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-blue-100 text-blue-700';
      case 'completed': return 'bg-green-100 text-green-700';
      case 'expired': return 'bg-red-100 text-red-700';
      case 'paused': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Activa';
      case 'completed': return 'Completada';
      case 'expired': return 'Expirada';
      case 'paused': return 'Pausada';
      default: return 'Desconocido';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <Play className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'expired': return <AlertCircle className="w-4 h-4" />;
      case 'paused': return <Clock className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Cargando sesión...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto mt-8 p-6 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-center gap-3 mb-4">
          <AlertCircle className="w-6 h-6 text-red-600" />
          <h2 className="text-xl font-semibold text-red-900">Error al cargar sesión</h2>
        </div>
        <p className="text-red-700 mb-4">{error}</p>
        <div className="flex gap-3">
          <button
            onClick={handleRefresh}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Reintentar
          </button>
          <button
            onClick={() => navigate('/epl/builder')}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
          >
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!sessionData) {
    return (
      <div className="max-w-2xl mx-auto mt-8 p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-center gap-3 mb-4">
          <AlertCircle className="w-6 h-6 text-yellow-600" />
          <h2 className="text-xl font-semibold text-yellow-900">Sesión no encontrada</h2>
        </div>
        <p className="text-yellow-700 mb-4">
          La sesión solicitada no existe o no tienes permisos para accederla.
        </p>
        <button
          onClick={() => navigate('/epl/builder')}
          className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700"
        >
          Volver al Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50 p-6 overflow-y-auto pb-24">
      <div className="max-w-4xl mx-auto">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/epl/builder')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver al Dashboard
          </button>

          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </div>

        {/* Session Info Card */}
        <div className="bg-white rounded-lg shadow-sm border mb-6">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-2xl font-bold text-gray-900">Sesión de Laboratorio</h1>
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(sessionData.status)}`}>
                {getStatusIcon(sessionData.status)}
                {getStatusText(sessionData.status)}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2 text-gray-600">
                <FileText className="w-4 h-4" />
                <span>ID de Sesión: {sessionData.id}</span>
              </div>
              
              <div className="flex items-center gap-2 text-gray-600">
                <User className="w-4 h-4" />
                <span>Usuario: {sessionData.userId || 'Anónimo'}</span>
              </div>
              
              <div className="flex items-center gap-2 text-gray-600">
                <Calendar className="w-4 h-4" />
                <span>Iniciada: {formatDate(sessionData.timestamps?.startedAt)}</span>
              </div>
              
              <div className="flex items-center gap-2 text-gray-600">
                <Clock className="w-4 h-4" />
                <span>Última actividad: {formatDate(sessionData.timestamps?.lastActivityAt)}</span>
              </div>
            </div>
          </div>

          {/* Session Progress */}
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Progreso de la Sesión</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-blue-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-blue-600 mb-1">
                  {sessionData.responses ? Object.keys(sessionData.responses).length : 0}
                </div>
                <div className="text-sm text-blue-700">Respuestas Guardadas</div>
              </div>

              <div className="p-4 bg-green-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-green-600 mb-1">
                  {sessionData.aiResults?.score || '--'}
                </div>
                <div className="text-sm text-green-700">Puntuación IA</div>
              </div>

              <div className="p-4 bg-purple-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-purple-600 mb-1">
                  {sessionData.aiResults?.processed ? 'Sí' : 'No'}
                </div>
                <div className="text-sm text-purple-700">Evaluado por IA</div>
              </div>
            </div>

            {/* Respuestas guardadas */}
            {sessionData.responses && Object.keys(sessionData.responses).length > 0 && (
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">Respuestas Actuales</h4>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="space-y-2">
                    {Object.entries(sessionData.responses).map(([fieldId, value]) => (
                      <div key={fieldId} className="flex justify-between items-start">
                        <div className="text-sm text-gray-600">
                          Campo {fieldId.substring(0, 8)}...
                        </div>
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {typeof value === 'string' ? value : JSON.stringify(value)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Resultados de IA */}
            {sessionData.aiResults?.processed && (
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">Evaluación IA</h4>
                <div className="bg-green-50 p-4 rounded-lg">
                  {sessionData.aiResults.feedback && (
                    <div className="mb-3">
                      <div className="text-sm font-medium text-green-800 mb-1">Feedback:</div>
                      <div className="text-sm text-green-700">{sessionData.aiResults.feedback}</div>
                    </div>
                  )}
                  
                  {sessionData.aiResults.recommendations && sessionData.aiResults.recommendations.length > 0 && (
                    <div>
                      <div className="text-sm font-medium text-green-800 mb-1">Recomendaciones:</div>
                      <ul className="text-sm text-green-700 space-y-1">
                        {sessionData.aiResults.recommendations.map((rec, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-green-600">•</span>
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Acciones Disponibles</h3>
          
          <div className="flex flex-wrap gap-4">
            {sessionData.status === 'active' && (
              <button
                onClick={handleContinue}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors"
              >
                <Play className="w-5 h-5" />
                Continuar Sesión
              </button>
            )}

            {sessionData.status === 'completed' && (
              <button
                onClick={handleViewResults}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors"
              >
                <BarChart3 className="w-5 h-5" />
                Ver Resultados Detallados
              </button>
            )}

            {sessionData.responses && Object.keys(sessionData.responses).length > 0 && (
              <button
                onClick={() => {
                  // TODO: Implementar vista de respuestas
                  console.log('Ver respuestas:', sessionData.responses);
                }}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors"
              >
                <Eye className="w-5 h-5" />
                Ver Respuestas
              </button>
            )}

            <button
              onClick={() => navigate(`/epl/lab/${sessionData.labId}`)}
              className="border border-gray-300 hover:border-gray-400 text-gray-700 px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
              Nueva Sesión
            </button>
          </div>
        </div>

        {/* Información adicional */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">Información sobre Sesiones</h4>
          <ul className="text-blue-800 space-y-1 text-sm">
            <li>• Las sesiones activas se guardan automáticamente cada 30 segundos</li>
            <li>• Puedes continuar una sesión desde cualquier dispositivo</li>
            <li>• Las sesiones completadas conservan todos los resultados y feedback</li>
            <li>• Los datos de sesión se mantienen por seguridad y análisis</li>
          </ul>
        </div>

      </div>
    </div>
  );
};

export default EplSessionRunner;